import React, { useState, useMemo } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { StudentShell } from '@/components/shared/StudentShell'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import type { PageProps, BadgeData, NotificationItem } from '@/types'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts'
import {
  Target, Trophy, Coins, Flame, ArrowRight, Bell, Medal, TrendingUp,
  BookOpen, Clock, GraduationCap, Award, Zap,
  Search, FileText, Swords, Bookmark, Brain, Crown, Wallet
} from 'lucide-react'
import { formatPercentile } from '@/lib/format'

interface RecentAttempt {
  id: number; test_title: string; score: number; max_score: number
  accuracy: number; completed_at: string; rank_percentile: number | null
}

interface SubjectAccuracy { subject: string; accuracy: number; correct: number; total: number }

interface Props {
  enrolled_exams: Array<{ id: number; name: string; code: string; tests_count: number }>
  recent_attempts: RecentAttempt[]
  score_trend: Array<{ date?: string; label: string; score: number; raw_score?: number; accuracy: number; attempts_count?: number }>
  subject_accuracy: SubjectAccuracy[]
  stats: {
    total_attempts: number;
    avg_accuracy: number;
    credit_balance: number;
    best_rank: number | null;
    best_percentile?: number | null;
    total_candidates?: number;
    streak_days: number;
  }
  recent_badges: BadgeData[]
  next_badge: BadgeData | null
  recent_notifications: NotificationItem[]
}

function formatRemainingMinutes(seconds: number): string {
  if (seconds <= 0) return 'Time up'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours > 0) {
    return `${hours}h ${minutes}m left`
  }
  return `${minutes}m left`
}

function PerformanceChartTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div className="bg-card border border-border p-2.5 rounded-xl shadow-md text-xs space-y-1 min-w-[140px]">
        <p className="font-bold text-foreground pb-1 border-b border-border/50">
          {label} {data.attempts_count && data.attempts_count > 1 ? `(${data.attempts_count} attempts)` : ''}
        </p>
        <p className="text-primary font-semibold flex items-center justify-between gap-3 pt-0.5">
          <span>Score %</span>
          <span className="font-bold">{data.score}%</span>
        </p>
        <p className="text-secondary font-semibold flex items-center justify-between gap-3">
          <span>Accuracy %</span>
          <span className="font-bold">{data.accuracy}%</span>
        </p>
      </div>
    )
  }
  return null
}

// Motivational Quotes Collection (Featuring Indian Leaders, Visionaries & Scientists)
const MOTIVATIONAL_QUOTES = [
  { quote: "Dream is not that which you see while sleeping, it is something that does not let you sleep.", author: "Dr. A.P.J. Abdul Kalam" },
  { quote: "Arise, awake, and stop not till the goal is reached.", author: "Swami Vivekananda" },
  { quote: "You cannot change your future, but you can change your habits, and surely your habits will change your future.", author: "Dr. A.P.J. Abdul Kalam" },
  { quote: "Take risks in your life. If you win, you can lead; if you lose, you can guide.", author: "Swami Vivekananda" },
  { quote: "You have to dream before your dreams can come true.", author: "Dr. A.P.J. Abdul Kalam" },
  { quote: "Be the change that you wish to see in the world.", author: "Mahatma Gandhi" },
  { quote: "Cultivation of mind should be the ultimate aim of human existence.", author: "Dr. B.R. Ambedkar" },
  { quote: "Give me blood, and I shall give you freedom! One individual may die for an idea, but that idea will, after his death, incarnate itself in a thousand lives.", author: "Netaji Subhash Chandra Bose" },
  { quote: "Manpower without unity is not a strength unless it is harmonized and united properly, then it becomes a spiritual power.", author: "Sardar Vallabhbhai Patel" },
  { quote: "You cannot cross the sea merely by standing and staring at the water.", author: "Rabindranath Tagore" },
  { quote: "Excellence happens not by accident. It is a process.", author: "Dr. A.P.J. Abdul Kalam" },
  { quote: "Ask not what your country can do for you, ask what you can do for your country.", author: "Dr. S. Radhakrishnan" },
  { quote: "In a day, when you don't come across any problems - you can be sure that you are traveling in a wrong path.", author: "Swami Vivekananda" },
  { quote: "Failure will never overtake me if my determination to succeed is strong enough.", author: "Dr. A.P.J. Abdul Kalam" },
  { quote: "I output light, not darkness.", author: "Dr. C.V. Raman" },
  { quote: "Knowledge is power. Information is liberating. Education is the premise of progress.", author: "Kofi Annan" },
  { quote: "If you want to shine like a sun, first burn like a sun.", author: "Dr. A.P.J. Abdul Kalam" },
  { quote: "Live as if you were to die tomorrow. Learn as if you were to live forever.", author: "Mahatma Gandhi" },
  { quote: "Strength is Life, Weakness is Death.", author: "Swami Vivekananda" },
  { quote: "Small daily improvements over time lead to stunning results.", author: "Robin Sharma" },
]

export default function StudentDashboard({ enrolled_exams, recent_attempts, score_trend, subject_accuracy, stats, recent_badges, next_badge, recent_notifications }: Props) {
  const { auth, ongoing_attempt } = usePage<PageProps>().props
  const firstName = auth.user?.name?.split(' ')[0] || 'Student'

  const [dailyQuote] = useState(() => MOTIVATIONAL_QUOTES[Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length)])
  const [timeRange, setTimeRange] = useState<'7 Days' | '30 Days' | 'All Time'>('7 Days')

  const filteredScoreTrend = useMemo(() => {
    if (!score_trend || score_trend.length === 0) return []
    const now = new Date()
    if (timeRange === '7 Days') {
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      const filtered = score_trend.filter(item => item.date && new Date(item.date) >= sevenDaysAgo)
      return filtered.length > 0 ? filtered : score_trend.slice(-7)
    }
    if (timeRange === '30 Days') {
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      const filtered = score_trend.filter(item => item.date && new Date(item.date) >= thirtyDaysAgo)
      return filtered.length > 0 ? filtered : score_trend.slice(-30)
    }
    return score_trend
  }, [score_trend, timeRange])

  const accuracyTheme = stats.avg_accuracy >= 75
    ? { text: 'text-secondary', bg: 'bg-secondary/10 border-secondary/20', bar: 'bg-secondary' }
    : stats.avg_accuracy >= 50
    ? { text: 'text-tertiary', bg: 'bg-tertiary/10 border-tertiary/20', bar: 'bg-tertiary' }
    : { text: 'text-destructive', bg: 'bg-destructive/10 border-destructive/20', bar: 'bg-destructive' }

  const statCards = [
    { icon: BookOpen, label: 'Tests Taken', value: stats.total_attempts, unit: 'Completed', color: 'text-primary', bg: 'bg-primary/10 border-primary/20' },
    {
      icon: Target,
      label: 'Avg Accuracy',
      value: `${stats.avg_accuracy}%`,
      unit: 'Overall Score',
      color: accuracyTheme.text,
      bg: accuracyTheme.bg,
      bar: { percentage: stats.avg_accuracy, color: accuracyTheme.bar }
    },
    { icon: Coins, label: 'Wallet Credits', value: stats.credit_balance, unit: 'Available', color: 'text-tertiary', bg: 'bg-tertiary/10 border-tertiary/20' },
    {
      icon: Award,
      label: 'All India Rank',
      value: stats.best_rank != null ? `#${stats.best_rank}` : '—',
      unit: (() => {
        if (stats.best_rank != null) {
          const candidateCount = stats.total_candidates ?? 0
          const candidateText = candidateCount > 1 ? ` • ${candidateCount.toLocaleString()} candidates` : ''
          if (stats.best_percentile != null) {
            return `${formatPercentile(stats.best_percentile)}${candidateText}`
          }
          return candidateCount > 0 ? `of ${candidateCount.toLocaleString()} candidates` : 'All India Standing'
        }
        return stats.total_attempts > 0 ? 'Calculating Rank' : 'Take a test to rank'
      })(),
      color: 'text-primary',
      bg: 'bg-primary/10 border-primary/20'
    },
  ]

  return (
    <StudentShell>
      {/* -------------------------------------------------------------
          MOBILE VIEW HOMEPAGE (< lg) — Matching Mockup Image 2 Screen 1
          ------------------------------------------------------------- */}
      <div className="block lg:hidden space-y-5 pb-6">
        {/* 1. Greeting & Streak Header */}
        <div className="flex items-center justify-between gap-2">
          <div className="space-y-0.5 min-w-0">
            <h1 className="text-xl font-extrabold text-foreground tracking-tight truncate">
              Hello, {firstName} 👋
            </h1>
            <p className="text-xs text-muted-foreground font-medium">
              Great to see you back!
            </p>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-tertiary/10 border border-tertiary/30 text-tertiary font-bold text-xs shadow-2xs shrink-0">
            <Flame className="h-4 w-4 fill-tertiary text-tertiary" />
            <span>{stats.streak_days || 1} Day Streak</span>
          </div>
        </div>

        {/* 2. Continue Your Preparation Card */}
        <Card className="p-4 sm:p-5 rounded-2xl border border-primary/20 bg-primary/5 shadow-2xs space-y-3 relative overflow-hidden">
          <div className="space-y-1">
            <p className="text-[11px] font-bold text-primary tracking-wider uppercase">
              Continue Your Preparation
            </p>
            <p className="text-xs text-muted-foreground">
              {ongoing_attempt ? 'Resume your last test' : 'Start your next practice test'}
            </p>
            <h3 className="text-sm sm:text-base font-bold text-foreground truncate pt-0.5">
              {ongoing_attempt?.test_title || enrolled_exams[0]?.name || 'SBI Clerk Prelims Mock'}
            </h3>
          </div>

          {ongoing_attempt && (
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs font-semibold text-muted-foreground">
                <span>{ongoing_attempt.active_section_name || 'Active Attempt'}</span>
                <span className="text-primary font-bold">
                  {formatRemainingMinutes(ongoing_attempt.is_sectional && ongoing_attempt.active_section_remaining_seconds != null ? ongoing_attempt.active_section_remaining_seconds : ongoing_attempt.remaining_seconds)}
                </span>
              </div>
              <Progress
                value={Math.max(10, Math.min(100, (1 - (ongoing_attempt.remaining_seconds / ((ongoing_attempt.duration_minutes || 60) * 60))) * 100))}
                className="h-2 rounded-full"
              />
            </div>
          )}

          <Button asChild className="w-full bg-primary text-primary-fg font-bold rounded-xl text-xs h-9 px-4 gap-1.5 shadow-xs">
            <Link href={ongoing_attempt?.resume_url || (enrolled_exams[0] ? `/student/exams/${enrolled_exams[0].id}` : '/student/tests')}>
              {ongoing_attempt ? 'Continue Test' : 'Start Test'} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </Card>

        {/* 3. Your Progress Section (2x2 Grid) */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-foreground tracking-tight">Your Progress</h2>
            <Link href="/student/leaderboard" className="text-xs font-semibold text-primary hover:underline">
              View All
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
            {/* Tests Taken */}
            <Card className="p-3.5 rounded-2xl border border-border bg-card shadow-2xs space-y-2">
              <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                <BookOpen className="h-4 w-4" />
              </div>
              <div>
                <p className="text-[11px] font-medium text-muted-foreground">Tests Taken</p>
                <p className="text-lg font-bold text-foreground">{stats.total_attempts}</p>
                <p className="text-[10px] text-muted-foreground">Completed</p>
              </div>
            </Card>

            {/* Accuracy */}
            <Card className="p-3.5 rounded-2xl border border-border bg-card shadow-2xs space-y-2">
              <div className="w-8 h-8 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary">
                <Target className="h-4 w-4" />
              </div>
              <div>
                <p className="text-[11px] font-medium text-muted-foreground">Accuracy</p>
                <p className="text-lg font-bold text-foreground">{stats.avg_accuracy}%</p>
                <p className="text-[10px] text-muted-foreground">Overall Score</p>
              </div>
            </Card>

            {/* All India Rank */}
            <Card className="p-3.5 rounded-2xl border border-border bg-card shadow-2xs space-y-2">
              <div className="w-8 h-8 rounded-xl bg-tertiary/10 border border-tertiary/20 flex items-center justify-center text-tertiary">
                <Trophy className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-muted-foreground">All India Rank</p>
                <p className="text-lg font-bold text-foreground">{stats.best_rank != null ? `#${stats.best_rank}` : '—'}</p>
                <p className="text-[10px] text-muted-foreground truncate">
                  {stats.best_percentile != null ? `Percentile ${stats.best_percentile}` : 'All India Standing'}
                </p>
              </div>
            </Card>

            {/* Wallet Credits */}
            <Card className="p-3.5 rounded-2xl border border-border bg-card shadow-2xs space-y-2">
              <div className="w-8 h-8 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary">
                <Wallet className="h-4 w-4" />
              </div>
              <div>
                <p className="text-[11px] font-medium text-muted-foreground">Wallet Credits</p>
                <p className="text-lg font-bold text-foreground">{stats.credit_balance}</p>
                <p className="text-[10px] text-muted-foreground">Available</p>
              </div>
            </Card>
          </div>
        </div>

        {/* 4. Performance Overview Section (Line Chart) */}
        <Card className="p-4 sm:p-5 rounded-2xl border border-border bg-card shadow-2xs space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-bold text-foreground tracking-tight">Performance Overview</h2>
            <div className="flex items-center gap-1 bg-muted/40 p-0.5 rounded-xl border border-border/50 text-xs">
              {(['7 Days', '30 Days', 'All Time'] as const).map(range => (
                <button
                  key={range}
                  type="button"
                  onClick={() => setTimeRange(range)}
                  className={`px-2 py-0.5 rounded-lg text-[10px] font-semibold transition-all ${
                    timeRange === range
                      ? 'bg-card text-foreground font-bold shadow-2xs border border-border/70'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 text-[11px] font-semibold">
            <span className="flex items-center gap-1.5 text-foreground/80">
              <span className="w-2 h-2 rounded-full bg-primary" /> Score %
            </span>
            <span className="flex items-center gap-1.5 text-foreground/80">
              <span className="w-2 h-2 rounded-full bg-secondary" /> Accuracy %
            </span>
          </div>

          {filteredScoreTrend.length === 0 ? (
            <div className="h-44 flex flex-col items-center justify-center text-center text-xs text-muted-foreground space-y-1">
              <TrendingUp className="h-6 w-6 text-muted-foreground/40" />
              <p>Complete mock tests to generate your performance curve.</p>
            </div>
          ) : (
            <div className="pt-1">
              <ResponsiveContainer width="100%" height={190}>
                <LineChart data={filteredScoreTrend} margin={{ top: 8, right: 8, bottom: 0, left: -22 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={{ stroke: 'hsl(var(--border))' }} />
                  <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={{ stroke: 'hsl(var(--border))' }} domain={[0, 100]} />
                  <Tooltip content={<PerformanceChartTooltip />} />
                  <Line type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3.5, fill: 'hsl(var(--primary))' }} name="Score %" />
                  <Line type="monotone" dataKey="accuracy" stroke="hsl(var(--secondary))" strokeWidth={2} dot={{ r: 3, fill: 'hsl(var(--secondary))' }} name="Accuracy %" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        {/* 5. Recent Test Attempts Section */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-foreground tracking-tight">Recent Test Attempts</h2>
            <Link href="/student/leaderboard" className="text-xs font-semibold text-primary hover:underline">
              View All
            </Link>
          </div>

          {recent_attempts.length === 0 ? (
            <Card className="p-6 text-center text-xs text-muted-foreground rounded-2xl">
              No test attempts yet. Start your first practice test!
            </Card>
          ) : (
            <div className="space-y-2">
              {recent_attempts.slice(0, 5).map(attempt => (
                <Link
                  key={attempt.id}
                  href={`/student/attempts/${attempt.id}/result`}
                  className="p-3.5 rounded-2xl border border-border/70 bg-card hover:bg-muted/30 transition-all flex items-center justify-between gap-3 shadow-2xs group"
                >
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <p className="text-xs font-bold text-foreground group-hover:text-primary transition-colors truncate">
                      {attempt.test_title}
                    </p>
                    <p className="text-[11px] text-muted-foreground font-medium">
                      {attempt.completed_at}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="text-[10px] font-bold text-secondary bg-secondary/10 px-2 py-0.5 rounded-md border border-secondary/20">
                      {attempt.accuracy}%
                    </span>
                    <span className={`text-xs font-bold ${attempt.score < 0 ? 'text-destructive' : 'text-primary'}`}>
                      {attempt.score} / {attempt.max_score} Pts
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* 6. Enrolled Exams Strip (Mobile Only) */}
        {enrolled_exams.length > 0 && (
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-foreground tracking-tight">My Exams</h2>
              <Link href="/student/exams" className="text-xs font-semibold text-primary hover:underline">
                Manage
              </Link>
            </div>
            <div className="space-y-2">
              {enrolled_exams.slice(0, 3).map(exam => (
                <Link
                  key={exam.id}
                  href={`/student/exams/${exam.id}`}
                  className="p-3.5 rounded-2xl border border-border/70 bg-card hover:bg-muted/30 transition-all flex items-center justify-between gap-3 shadow-2xs group"
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 text-primary">
                      <GraduationCap className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-foreground group-hover:text-primary transition-colors truncate">
                        {exam.name}
                      </p>
                      <p className="text-[10px] text-muted-foreground">{exam.code}</p>
                    </div>
                  </div>
                  <span className="text-[11px] text-muted-foreground font-medium shrink-0">
                    {exam.tests_count} Tests
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* -------------------------------------------------------------
          DESKTOP VIEW (>= lg) — Full Multi-Column Layout
          ------------------------------------------------------------- */}
      <div className="hidden lg:block space-y-6 sm:space-y-8 max-w-7xl mx-auto pb-16">
        {/* Banner with Quote */}
        <Card className="p-4 sm:p-6 lg:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5 relative overflow-hidden">
          <div className="space-y-2 z-10">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="glass" className="text-[10px] font-bold uppercase tracking-wider text-primary">
                Student Portal
              </Badge>
              {stats.streak_days > 0 && (
                <Badge variant="outline" className="gap-1.5 font-extrabold bg-tertiary/10 text-tertiary border-tertiary/30 shadow-2xs">
                  <Flame className="h-3.5 w-3.5 fill-tertiary text-tertiary animate-pulse" />
                  <span>{stats.streak_days} Day Streak!</span>
                </Badge>
              )}
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-foreground tracking-tight">
              Welcome back, {firstName}
            </h1>
            <div className="max-w-2xl bg-muted/40 p-2.5 sm:p-3 rounded-xl border border-border/40">
              <p className="text-xs sm:text-sm text-foreground/90 font-serif italic leading-relaxed">
                "{dailyQuote.quote}" <span className="not-italic text-muted-foreground font-sans font-semibold text-xs">— {dailyQuote.author}</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 shrink-0 z-10 w-full sm:w-auto">
            <Button variant="outline" size="sm" className="flex-1 sm:flex-initial font-extrabold gap-1.5 rounded-xl border-primary/40 text-primary hover:bg-primary/10 h-9 text-xs" asChild>
              <Link href="/student/tests">
                <Zap className="h-3.5 w-3.5 text-primary" /> Practice Tests
              </Link>
            </Button>
            <Button variant="outline" size="sm" className="flex-1 sm:flex-initial font-bold gap-1.5 rounded-xl border-border hover:bg-muted/50 h-9 text-xs" asChild>
              <Link href="/student/exams">
                <GraduationCap className="h-3.5 w-3.5 text-muted-foreground" /> My Exams
              </Link>
            </Button>
          </div>
        </Card>

        {/* Stats Vertical Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {statCards.map(s => {
            const Icon = s.icon
            return (
              <Card key={s.label} className="p-4 sm:p-5 flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">{s.label}</p>
                  <p className="text-xl sm:text-2xl font-bold text-foreground tracking-tight">{s.value}</p>
                  <p className="text-[11px] text-muted-foreground font-medium">{s.unit}</p>
                  {'bar' in s && s.bar && (
                    <div className="w-20 h-1.5 rounded-full bg-muted overflow-hidden mt-1.5">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${s.bar.color}`}
                        style={{ width: `${Math.min(100, Math.max(0, s.bar.percentage))}%` }}
                      />
                    </div>
                  )}
                </div>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl ${s.bg} border flex items-center justify-center shrink-0`}>
                  <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${s.color}`} />
                </div>
              </Card>
            )
          })}
        </div>

        {/* Analytics Section */}
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Score Trend Chart with horizontal scroll wrapper for mobile */}
          <Card className="lg:col-span-2 overflow-hidden">
            <CardHeader className="pb-2 border-b border-border/40">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="text-sm sm:text-base font-bold flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 sm:h-4.5 sm:w-4.5 text-primary" /> Score & Performance Progression
                </CardTitle>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 bg-muted/40 p-0.5 rounded-xl border border-border/50 text-xs">
                    {(['7 Days', '30 Days', 'All Time'] as const).map(range => (
                      <button
                        key={range}
                        type="button"
                        onClick={() => setTimeRange(range)}
                        className={`px-2 py-0.5 rounded-lg text-[10px] font-semibold transition-all ${
                          timeRange === range
                            ? 'bg-card text-foreground font-bold shadow-2xs border border-border/70'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {range}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 text-xs font-semibold">
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-primary" /> Score</span>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-secondary" /> Accuracy %</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4 overflow-x-auto">
              {filteredScoreTrend.length === 0 ? (
                <div className="h-56 flex flex-col items-center justify-center text-center text-sm text-muted-foreground space-y-2">
                  <TrendingUp className="h-8 w-8 text-muted-foreground/30" />
                  <p>Complete mock tests to generate your performance trend line.</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={filteredScoreTrend} margin={{ top: 10, right: 10, bottom: 5, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={{ stroke: 'hsl(var(--border))' }} />
                    <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={{ stroke: 'hsl(var(--border))' }} domain={[0, 100]} />
                    <Tooltip content={<PerformanceChartTooltip />} />
                    <Line type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 4, fill: 'hsl(var(--primary))' }} name="Score %" />
                    <Line type="monotone" dataKey="accuracy" stroke="hsl(var(--secondary))" strokeWidth={2} dot={{ r: 3, fill: 'hsl(var(--secondary))' }} name="Accuracy %" strokeDasharray="4 2" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Subject Accuracy */}
          <Card>
            <CardHeader className="pb-2 border-b border-border/40">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <Target className="h-4.5 w-4.5 text-secondary" /> Subject Mastery
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {subject_accuracy.length === 0 ? (
                <div className="h-56 flex flex-col items-center justify-center text-center text-sm text-muted-foreground space-y-2">
                  <Target className="h-8 w-8 text-muted-foreground/30" />
                  <p>No subject breakdown data recorded yet.</p>
                </div>
              ) : (
                <div className="space-y-3.5">
                  {subject_accuracy.map(s => (
                    <div key={s.subject} className="space-y-1">
                      <div className="flex justify-between text-xs font-semibold">
                        <span className="text-foreground truncate">{s.subject}</span>
                        <span className={s.accuracy >= 70 ? 'text-secondary font-bold' : s.accuracy >= 40 ? 'text-tertiary font-bold' : 'text-muted-foreground font-bold'}>
                          {s.accuracy}%
                        </span>
                      </div>
                      <Progress value={s.accuracy} className="h-2 rounded-full" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detailed Sections Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Attempt Records */}
          <Card>
            <CardHeader className="pb-3 border-b border-border/40 flex items-center justify-between gap-2">
              <CardTitle className="text-sm sm:text-base font-bold flex items-center gap-2 min-w-0 truncate">
                <Clock className="h-4 w-4 sm:h-4.5 sm:w-4.5 text-primary shrink-0" /> <span className="truncate">Recent Test Attempts</span>
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-xs font-semibold text-primary hover:text-primary/80 shrink-0 h-8 px-2" asChild>
                <Link href="/student/leaderboard">View All <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {recent_attempts.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  No mock test attempts recorded yet.
                </div>
              ) : (
                <div className="divide-y divide-border/40">
                  {recent_attempts.map(a => (
                    <Link
                      key={a.id}
                      href={`/student/attempts/${a.id}/result`}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 sm:p-4 hover:bg-primary/5 transition-colors group gap-1.5 sm:gap-4 min-w-0"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 sm:truncate leading-snug">
                          {a.test_title}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{a.completed_at}</p>
                      </div>
                      <div className="flex items-center sm:flex-col sm:items-end justify-between sm:justify-start gap-2 shrink-0 pt-1 sm:pt-0 border-t sm:border-t-0 border-border/20">
                        <span className="text-xs sm:text-sm font-extrabold text-primary">{a.score} / {a.max_score} Pts</span>
                        <span className="text-[11px] font-semibold text-secondary bg-secondary/10 sm:bg-transparent px-2 py-0.5 sm:p-0 rounded border border-secondary/20 sm:border-0">{a.accuracy}% accuracy</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Enrolled Exams & Badges */}
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-3 border-b border-border/40 flex items-center justify-between gap-2">
                <CardTitle className="text-sm sm:text-base font-bold flex items-center gap-2 min-w-0 truncate">
                  <BookOpen className="h-4 w-4 sm:h-4.5 sm:w-4.5 text-primary shrink-0" /> <span className="truncate">Enrolled Entrance Exams</span>
                </CardTitle>
                <Button variant="ghost" size="sm" className="text-xs font-semibold text-primary hover:text-primary/80 shrink-0 h-8 px-2" asChild>
                  <Link href="/student/exams">Manage <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {enrolled_exams.length === 0 ? (
                  <div className="p-6 text-center space-y-2">
                    <p className="text-sm text-muted-foreground">You are not enrolled in any competitive exams.</p>
                    <Button size="sm" asChild><Link href="/student/exams">Browse & Enroll</Link></Button>
                  </div>
                ) : (
                  <div className="divide-y divide-border/40">
                    {enrolled_exams.slice(0, 3).map(e => (
                      <Link
                        key={e.id}
                        href={`/student/exams/${e.id}`}
                        className="flex items-center justify-between p-3.5 hover:bg-primary/5 transition-colors group gap-2 min-w-0"
                      >
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <span className="text-xs sm:text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate">
                            {e.name}
                          </span>
                        </div>
                        <span className="text-[11px] sm:text-xs text-muted-foreground font-medium shrink-0 ml-2">{e.tests_count} Tests</span>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Badges Preview */}
            {(recent_badges.length > 0 || next_badge) && (
              <Card>
                <CardHeader className="pb-3 border-b border-border/40 flex flex-row items-center justify-between">
                  <CardTitle className="text-base font-bold flex items-center gap-2">
                    <Medal className="h-4.5 w-4.5 text-tertiary" /> Badges & Milestones
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="text-xs font-semibold text-primary hover:text-primary/80" asChild>
                    <Link href="/student/badges">All Badges <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
                  </Button>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  {recent_badges.length > 0 && (
                    <div className="grid grid-cols-2 gap-2">
                      {recent_badges.slice(0, 2).map(b => {
                        const BADGE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
                          first_test: Target,
                          question_explorer: Search,
                          test_taker: FileText,
                          credit_earner: Coins,
                          streak_3: Flame,
                          consistent_learner: BookOpen,
                          mock_warrior: Swords,
                          bookworm: Bookmark,
                          streak_7: Flame,
                          question_master: Brain,
                          exam_champion: Trophy,
                          top_ranker: Crown,
                        }
                        const Icon = BADGE_ICONS[b.key] || Target

                        return (
                          <div key={b.key} className="flex items-center gap-2 p-2 rounded-xl bg-muted/40 border border-border/40 min-w-0">
                            <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary border border-primary/20 flex items-center justify-center shrink-0">
                              <Icon className="h-3.5 w-3.5 text-primary" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-bold text-foreground truncate">{b.name}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{b.awarded_at}</p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {next_badge && (
                    <div className="pt-2 border-t border-border/40 space-y-1.5">
                      <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Next Milestone to Unlock</p>
                      <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-primary/10 border border-primary/20 min-w-0">
                        {(() => {
                          const BADGE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
                            first_test: Target,
                            question_explorer: Search,
                            test_taker: FileText,
                            credit_earner: Coins,
                            streak_3: Flame,
                            consistent_learner: BookOpen,
                            mock_warrior: Swords,
                            bookworm: Bookmark,
                            streak_7: Flame,
                            question_master: Brain,
                            exam_champion: Trophy,
                            top_ranker: Crown,
                          }
                          const Icon = BADGE_ICONS[next_badge.key] || Target
                          return (
                            <div className="w-7 h-7 rounded-lg bg-primary/20 text-primary flex items-center justify-center shrink-0">
                              <Icon className="h-3.5 w-3.5 text-primary" />
                            </div>
                          )
                        })()}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between text-xs font-semibold mb-1">
                            <span className="text-foreground truncate">{next_badge.name}</span>
                            <span className="text-primary font-bold">{next_badge.progress_pct ?? 0}%</span>
                          </div>
                          <Progress value={next_badge.progress_pct ?? 0} className="h-1.5 rounded-full" />
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Notifications */}
        {recent_notifications.length > 0 && (
          <Card>
            <CardHeader className="pb-3 border-b border-border/40 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <Bell className="h-4.5 w-4.5 text-primary" /> Notifications & Announcements
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-xs font-semibold text-primary hover:text-primary/80" asChild>
                <Link href="/student/notifications">View All</Link>
              </Button>
            </CardHeader>
            <CardContent className="p-0 divide-y divide-border/40">
              {recent_notifications.map(n => (
                <div key={n.id} className={`flex gap-3.5 p-4 ${!n.read ? 'bg-primary/5' : ''}`}>
                  <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">{n.title}</p>
                    {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{n.body}</p>}
                    <p className="text-[11px] text-muted-foreground mt-1 font-medium">{n.created_at}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </StudentShell>
  )
}
